.sidebar {
  flex: 0 0 var(--sidebar-width);
  width: var(--sidebar-width);
  min-width: 280px;
  background: var(--color-bg-panel);
  border: 0;
  border-left: 1px solid var(--color-border);
  border-radius: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
  overflow: hidden;
}

/* ── Fullscreen + Chat: floating overlay ──────────────────────────────────── */
/* WHY: in fullscreen+chat mode the sidebar floats over the video as a
   semi-transparent panel on the right. It can be collapsed to a thin tab. */
.floating {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 340px;
  max-width: 340px;
  min-width: 0;
  border-radius: 0;
  border: none;
  border-left: 1px solid var(--color-border);
  background: rgba(10, 10, 10, 0.86);
  backdrop-filter: blur(12px);
  z-index: 20;
  transition: transform 160ms var(--ease-out);
}

.collapsed {
  transform: translateX(calc(100% - 28px));
}

.collapseTab {
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 24px;
  height: 56px;
  background: rgba(10, 10, 10, 0.86);
  border: none;
  border-right: 1px solid var(--color-border);
  border-radius: 8px 0 0 8px;
  color: var(--color-text);
  font-size: 14px;
  cursor: pointer;
  z-index: 21;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background var(--motion-fast) var(--ease-out);
}

.collapseTab:hover {
  background: var(--color-bg-elevated);
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.header {
  padding: 18px;
  border-bottom: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.titleRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.title {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.codeRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--fs-sm);
}

.codeLabel {
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.code {
  font-family: var(--font-mono);
  background: var(--color-bg-surface);
  padding: 3px 9px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border-strong);
  font-size: var(--fs-xs);
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--color-text-code);
}

/* ── Subtitle upload row ─────────────────────────────────────────────────── */
.subRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--fs-sm);
}

/* WHY: hide the native file input — the styled button triggers it via .click() */
.subFileInput {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.subButton {
  flex: 1 1 auto;
}

/* ── Chat (flex, takes most space) ────────────────────────────────────────── */
.chat {
  flex: 1 1 auto;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--color-border);
}

.chatHeader {
  padding: 11px 18px 6px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-subtle);
  font-weight: 700;
  flex-shrink: 0;
}

.chatMessages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 18px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-height: 0;
}

.chatEmpty {
  color: var(--color-text-subtle);
  font-size: var(--fs-sm);
  text-align: center;
  padding: 16px 0;
}

.chatMessage {
  font-size: var(--fs-sm);
  line-height: 1.45;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.chatAuthor {
  font-weight: 700;
  margin-right: var(--space-1);
}

.chatText {
  color: var(--color-text-muted);
}

.chatForm {
  display: flex;
  gap: var(--space-2);
  padding: 11px 18px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.chatInput {
  flex: 1;
  min-width: 0;
  height: 36px;
  font-size: var(--fs-sm);
}

/* ── Claim host ────────────────────────────────────────────────────────────── */
.claimHost {
  padding: 12px 18px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
}

.claimForm {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.claimInput {
  height: 34px;
  font-size: var(--fs-sm);
}

.claimButtons {
  display: flex;
  gap: var(--space-2);
}

.claimToggle {
  width: 100%;
  height: 38px;
}

/* ── Members (fixed height, scrollable) ─────────────────────────────────────── */
.membersHead {
  padding: 14px 18px 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  flex-shrink: 0;
}

.membersCount {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-subtle);
  font-weight: 700;
}

.members {
  list-style: none;
  margin: 0;
  padding: 0 10px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  flex: 0 0 auto;
  max-height: 200px;
  min-height: 60px;
}

.empty {
  padding: var(--space-3);
  color: var(--color-text-subtle);
  font-size: var(--fs-sm);
  text-align: center;
}

.member {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 9px 10px;
  border-radius: 9px;
  transition: background var(--motion-fast) var(--ease-out);
}

.member:hover {
  background: var(--color-bg-surface);
}

.memberName {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 500;
}

.mutedBadge {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.8;
  vertical-align: middle;
}

.memberActions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

/* ── Phone: full-width panel under the video, scrolls on its own ──────────── */
@media (max-width: 900px) {
  .sidebar {
    flex: 1 1 0;
    width: 100%;
    min-width: 0;
    min-height: 0;
    border-left: 0;
    border-top: 1px solid var(--color-border);
    overflow-y: auto;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .header {
    padding: var(--space-3) var(--space-4);
  }
  .chat {
    flex: 1 0 240px;
  }
  .chatForm,
  .chatMessages,
  .chatHeader {
    padding-left: var(--space-4);
    padding-right: var(--space-4);
  }
}
